import { useLanguage } from "@/context/LanguageContext";
import { Smartphone, Monitor, Tablet } from "lucide-react";

const mobileScreenshots = {
  dashboard: "https://customer-assets.emergentagent.com/job_quote-manager-65/artifacts/j5zqptg9_image.png",
  quote: "https://customer-assets.emergentagent.com/job_quote-manager-65/artifacts/zfy54npd_image.png",
  products: "https://customer-assets.emergentagent.com/job_quote-manager-65/artifacts/8pvfhfgc_image.png",
};

export function MultiDeviceSection() {
  const { t } = useLanguage();

  return (
    <section data-testid="multidevice-section" className="py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
            {t("multidevice.overline")}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {t("multidevice.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">
            {t("multidevice.subtitle")}
          </p>
        </div>

        {/* Device icons */}
        <div className="flex items-center justify-center gap-8 sm:gap-12 mb-12">
          {[
            { icon: Smartphone, label: t("multidevice.phone"), color: "bg-blue-50 text-blue-600" },
            { icon: Tablet, label: t("multidevice.tablet"), color: "bg-cyan-50 text-cyan-600" },
            { icon: Monitor, label: t("multidevice.computer"), color: "bg-violet-50 text-violet-600" },
          ].map((device, i) => {
            const Icon = device.icon;
            return (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`device-${i}`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${device.color}`}>
                  <Icon className="w-7 h-7" />
                </div>
                <span className="text-sm font-medium text-slate-700">{device.label}</span>
              </div>
            );
          })}
        </div>

        {/* Mobile screenshots showcase */}
        <div className="flex items-end justify-center gap-4 sm:gap-8 max-w-4xl mx-auto">
          {/* Left phone - Quote form */}
          <div className="w-[200px] sm:w-[240px] shrink-0 transform -rotate-3 hover:rotate-0 transition-transform duration-500" data-testid="device-screenshot-quote">
            <div className="rounded-[24px] overflow-hidden border-[3px] border-slate-800 shadow-2xl shadow-slate-300/50 bg-white">
              <div className="bg-slate-800 h-6 flex items-center justify-center">
                <div className="w-16 h-3 bg-slate-700 rounded-full" />
              </div>
              <img
                src={mobileScreenshots.quote}
                alt={t("imageAlt.mobileQuote")}
                width="240"
                height="500"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* Center phone (larger) - Dashboard */}
          <div className="w-[240px] sm:w-[280px] shrink-0 transform hover:scale-[1.02] transition-transform duration-500 z-10" data-testid="device-screenshot-dashboard">
            <div className="rounded-[28px] overflow-hidden border-[3px] border-slate-800 shadow-2xl shadow-slate-300/50 bg-white">
              <div className="bg-slate-800 h-6 flex items-center justify-center">
                <div className="w-16 h-3 bg-slate-700 rounded-full" />
              </div>
              <img
                src={mobileScreenshots.dashboard}
                alt={t("imageAlt.mobileDashboard")}
                width="280"
                height="580"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* Right phone - Products */}
          <div className="w-[200px] sm:w-[240px] shrink-0 transform rotate-3 hover:rotate-0 transition-transform duration-500" data-testid="device-screenshot-products">
            <div className="rounded-[24px] overflow-hidden border-[3px] border-slate-800 shadow-2xl shadow-slate-300/50 bg-white">
              <div className="bg-slate-800 h-6 flex items-center justify-center">
                <div className="w-16 h-3 bg-slate-700 rounded-full" />
              </div>
              <img
                src={mobileScreenshots.products}
                alt={t("imageAlt.mobileCatalog")}
                width="240"
                height="500"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
